<template>
  <div class="consumer-category">
    <h2>카테고리</h2>
    <hr />
    <div>
      <div class="parent-category-container">
        <div v-for="item in parentCategoryDatas" :key="item.categoryId">
          <input
            type="radio"
            v-model="selectedParentCategoryId"
            :id="item.categoryId"
            name="parentCategory"
            :value="item.categoryId"
          />
          <label :for="item.categoryId">{{ item.categoryName }}</label>
        </div>
      </div>
      <div class="child-category-container">
        <h3>
          <router-link :to="`/list?category=${selectedParentCategoryId}`">
            {{ selectedParentCategoryName }} >
          </router-link>
        </h3>
        <div v-for="item in childCategoryDatas" :key="item.categoryId">
          <router-link :to="`/list?category=${item.categoryId}`">
            {{ item.categoryName }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="js">
import { computed, onMounted, ref, watch } from "vue";
import { db } from "@/lib/firebase";
import { getDocs, query, collection, where, orderBy } from "firebase/firestore";
import { useBusyLock } from "@/lib/useBusyLock";

const { runLocked } = useBusyLock();

const parentCategoryDatas = ref([]);
const selectedParentCategoryId = ref("43");
const childCategoryDatas = ref([]);

// --- 선택된 상위 카테고리명
const selectedParentCategoryName = computed(() => {
  return (
    parentCategoryDatas.value.find(
      (item) => String(item.categoryId) === String(selectedParentCategoryId.value)
    )?.categoryName || ""
  );
});

// --- 하위 카테고리 조회 (watch/onMounted에서 재사용)
const fetchChildCategoryData = async () => {
  try {
    const parentId = String(selectedParentCategoryId.value || "");
    if (!parentId) {
      childCategoryDatas.value = [];
      return;
    }

    console.log("Fetching Child Category Data..");

    const childCategoryRef = await getDocs(
      query(
        collection(db, "category"),
        where("categoryParentId", "==", parentId),
        orderBy("categoryOrder", "asc")
      )
    );

    childCategoryDatas.value = childCategoryRef.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    console.log("Fetched Child Category Data:", childCategoryDatas.value);
  } catch (error) {
    console.error("Failed to fetch child categories:", error);
    childCategoryDatas.value = [];
  }
};

// --- 초기 로딩: 상위 카테고리 + 첫 하위 카테고리
onMounted(() => {
  runLocked(async () => {
    console.log("Fetching Parent Category Datas..");

    const parentCategoryRef = await getDocs(
      query(
        collection(db, "category"),
        where("categoryId", "!=", "1"),
        where("categoryGrade", "==", 0),
        orderBy("categoryOrder", "asc")
      )
    );

    parentCategoryDatas.value = parentCategoryRef.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    console.log("Fetched Parent Category Datas:", parentCategoryDatas.value);

    // --- 기본값(43)이 목록에 없으면 첫 번째 카테고리로 자동 보정
    const hasSelected = parentCategoryDatas.value.some(
      (item) => String(item.categoryId) === String(selectedParentCategoryId.value)
    );

    if (!hasSelected && parentCategoryDatas.value.length > 0) {
      selectedParentCategoryId.value = String(parentCategoryDatas.value[0].categoryId);
      // --- watch가 하위 카테고리 조회를 실행함
      return;
    }

    await fetchChildCategoryData();
  }).catch((error) => {
    console.error("초기 카테고리 로딩 실패:", error);
  });
});

// --- 상위 카테고리 변경 시 하위 카테고리 재조회
// --- 여기서는 runLocked를 쓰지 않음 (UI 선택 변경 반응성 유지)
watch(
  () => selectedParentCategoryId.value,
  async (newVal, oldVal) => {
    if (String(newVal) === String(oldVal)) return;
    await fetchChildCategoryData();
  }
);
</script>

<style lang="scss" scoped>
.consumer-category {
  height: 100%;
  > h2 {
    text-align: center;
    margin-top: 16px;
  }

  > hr {
    margin-top: 16px;
    border: none;
    border-top: 1px solid #999;
  }

  > div {
    display: flex;
    height: 100%;

    > .parent-category-container {
      min-width: 128px;
      max-width: 128px;
      background: #efefef;
      height: 100%;

      input[type="radio"] {
        display: none;
      }

      label {
        height: 48px;
        cursor: pointer;
        background: #efefef;
        display: flex;
        align-items: center;
        padding: 0 24px;
      }

      input:checked + label {
        background: white;
        color: #007bff;
        font-weight: 700;
      }
    }

    > .child-category-container {
      padding: 12px 16px;
      flex: 1;

      > div {
        &:nth-child(2) {
          margin-top: 8px;
        }

        > a {
          width: 100%;
          height: 42px;
          display: flex;
          align-items: center;
        }
      }
    }
  }
}
</style>
